import React, { useState, useMemo } from 'react';
import { PageHeader, Input, Space, Table, Tag, Button, Typography, DatePicker } from 'antd';
import { CalendarOutlined } from '@ant-design/icons';
import moment from 'moment';
import firebase from 'firebase/app';

import CenteredSpinner from '../../../components/CenteredSpinner';
import { useUsers } from '../../../models/useUsers';
import { getAdminDatePresets, getCurrentWeekRange } from '../../../utils/adminDatePresets';
import { getAppBaseUrl } from '../../../utils/URLBuilder';

import '../../../App.scss';
import 'antd/dist/antd.css';

const { RangePicker } = DatePicker;
const { Text } = Typography;
const URLPath = getAppBaseUrl();
const LIMIT = 200;

export default function AdminPayersScreen() {
  const [searchInput, setSearchInput] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState(null);
  const [dateRange, setDateRange] = useState(getCurrentWeekRange);
  const [, setPresetRefresh] = useState(0);

  const startDate = dateRange && dateRange[0] ? dateRange[0].toDate() : null;
  const endDate = dateRange && dateRange[1] ? dateRange[1].toDate() : null;

  const [loading, users] = useUsers({
    startDate,
    endDate,
    limit: LIMIT,
    isAnonymous: true,
  });

  const filteredUsers = useMemo(() => {
    return users || [];
  }, [users]);

  const handleLookup = async () => {
    const trimmed = (searchInput || '').trim();
    if (!trimmed) return;

    const isEmail = trimmed.includes('@');
    if (!isEmail) {
      window.location.href = `${URLPath}/admin/payers/${trimmed}`;
      return;
    }

    setLookupLoading(true);
    setLookupError(null);
    try {
      const snap = await firebase.firestore()
        .collection('users')
        .where('email', '==', trimmed)
        .limit(1)
        .get();
      if (snap.empty) {
        setLookupError(`No user found with email: ${trimmed}`);
      } else {
        window.location.href = `${URLPath}/admin/payers/${snap.docs[0].id}`;
      }
    } catch (err) {
      setLookupError(`Lookup failed: ${err.message}`);
    } finally {
      setLookupLoading(false);
    }
  };

  const columns = [
    {
      title: 'User ID',
      dataIndex: 'id',
      key: 'id',
      render: (uid) => (
        <a href={`${URLPath}/admin/payers/${uid}`}>
          <code style={{ fontSize: 11 }}>{uid}</code>
        </a>
      ),
    },
    {
      title: 'Name',
      dataIndex: 'displayName',
      key: 'displayName',
      render: (v) => v ? <Text style={{ fontSize: 12 }}>{v}</Text> : <Text type="secondary" style={{ fontSize: 11 }}>—</Text>,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: (v) => v ? <Text style={{ fontSize: 12 }}>{v}</Text> : <Text type="secondary" style={{ fontSize: 11 }}>—</Text>,
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (v) => {
        if (!v) return '-';
        const ms = v.toMillis ? v.toMillis() : null;
        if (!ms) return '-';
        return (
          <Space size={6} style={{ fontSize: 12 }}>
            <CalendarOutlined />
            <span>{moment(ms).format('YYYY-MM-DD HH:mm')}</span>
          </Space>
        );
      },
    },
    {
      title: 'Anonymous',
      dataIndex: 'isAnonymous',
      key: 'isAnonymous',
      render: (v) => <Tag color={v ? 'orange' : 'blue'}>{v ? 'anon' : 'linked'}</Tag>,
    },
    {
      title: 'Origin',
      dataIndex: 'signupOriginPath',
      key: 'origin',
      render: (v, u) => {
        const path = v || u.originPath || u.lastOriginPath || null;
        const reason = u.signupOriginReason || u.originReason || u.lastOriginReason || null;
        return (
          <Space direction="vertical" size={0}>
            {reason ? <Tag color="purple" style={{ width: 'fit-content' }}>{reason}</Tag> : null}
            {path ? <Text style={{ fontSize: 11 }} type="secondary">{path.length > 48 ? `${path.slice(0, 45)}…` : path}</Text> : null}
          </Space>
        );
      },
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, u) => (
        <Button size="small" href={`${URLPath}/admin/payers/${u.id}`}>
          View
        </Button>
      ),
    },
  ];

  return (
    <div style={{ height: '100%', textAlign: 'center' }}>
      <PageHeader
        ghost={false}
        title="Payers"
        subTitle="Anonymous payers on pay.strikepay.co"
      />

      <div style={{ padding: '0 12px 20px 12px' }}>
        <Input.Group compact style={{ display: 'flex', maxWidth: 560 }}>
          <Input
            placeholder="Look up by email address or User ID..."
            value={searchInput}
            onChange={(e) => { setSearchInput(e.target.value); setLookupError(null); }}
            onPressEnter={handleLookup}
            allowClear
            style={{ flex: 1 }}
          />
          <Button
            type="primary"
            onClick={handleLookup}
            disabled={!searchInput.trim()}
            loading={lookupLoading}
          >
            View Payer
          </Button>
        </Input.Group>
        {lookupError && (
          <Text type="danger" style={{ display: 'block', marginTop: 6, fontSize: 13 }}>
            {lookupError}
          </Text>
        )}
      </div>

      <Space style={{ width: '100%', padding: '0 12px 12px 12px' }} direction="vertical">
        <Space wrap>
          <RangePicker
            value={dateRange}
            onChange={(range) => setDateRange(range && range[0] && range[1] ? range : getCurrentWeekRange())}
            onOpenChange={(open) => { if (open) setPresetRefresh((t) => t + 1); }}
            ranges={getAdminDatePresets()}
            allowClear={false}
          />
          <span style={{ color: (filteredUsers || []).length >= LIMIT ? '#ff4d4f' : undefined }}>
            {(filteredUsers || []).length} result{(filteredUsers || []).length !== 1 ? 's' : ''} (max {LIMIT})
          </span>
        </Space>
      </Space>

      {loading ? (
        <CenteredSpinner loading={true} noLayout={false} />
      ) : (
        <Table
          style={{ textAlign: 'left', padding: '0 12px 12px 12px' }}
          rowKey="id"
          dataSource={filteredUsers}
          columns={columns}
          scroll={{ x: 1100 }}
          pagination={{ pageSize: 50, showSizeChanger: false }}
        />
      )}
    </div>
  );
}
